import { Checkbox } from 'primereact/checkbox'
import { useTranslation } from 'react-i18next'
import { Paginator } from 'primereact/paginator'
import EmptyState from '../../../components/ui/EmptyState'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import { lateDaysText, taskDurationText, type MeetingManagementTask } from '../types'
import ResponsiblesLines from './ResponsiblesLines'
import AttachmentCell from './AttachmentCell'
import TaskOriginMark from './TaskOriginMark'

interface Props {
  rows: MeetingManagementTask[]
  loading: boolean
  totalRecords: number
  first: number
  rows_: number
  onPage: (first: number, rows: number) => void
  selection: MeetingManagementTask[]
  onToggle: (row: MeetingManagementTask, checked: boolean) => void
  /** `false` → seçim kutusu hiç çizilmez (Kısıtlı Yetkili asistan) — tablodaki prop'un karşılığı. */
  selectable: boolean
  /** Ataca tıklanınca çağrılır — dosya pop-up'ını sayfa açar (kart dalı kendi pop-up'ını taşımaz). */
  onOpenFiles: (row: MeetingManagementTask) => void
  /** Kart başlığına tıklanınca çağrılır — detay pop-up'ını sayfa açar. */
  onOpenDetail: (row: MeetingManagementTask) => void
  /** Boş liste metni — tablodaki prop'un karşılığı (verilmezse Toplantı Yönetimi metni). */
  emptyText?: { title: string; description: string }
}

/**
 * Dar ekran kart dalı (105) — tablo <b>yerine</b> (aynı anda değil) çizilir; veri, sayfalama ve satır
 * işlemleri aynıdır, değişen yalnız sunumdur.
 *
 * <p>Eşik <b>tek kaynaktan</b> gelir ({@code CARD_VIEW_BREAKPOINT}) ve bu bileşene piksel değeri
 * yazılmaz — sayfa hangi dalı çizeceğine orada karar verir.
 *
 * <p>Masaüstünü düzeltip kart dalını bırakmak dar ekran deneyimini düzeltmez: seçim kutusu, geciken
 * vurgusu ve alt görev ilişkisi burada da vardır.
 */
export default function MeetingManagementCards({
  rows,
  loading,
  totalRecords,
  first,
  rows_,
  onPage,
  selection,
  onToggle,
  selectable: selectionEnabled,
  onOpenFiles,
  onOpenDetail,
  emptyText,
}: Readonly<Props>) {
  const { t } = useTranslation()

  if (loading) {
    return <LoadingSkeleton />
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        icon="pi pi-inbox"
        title={emptyText?.title ?? t('meetingManagement.empty.title')}
        description={emptyText?.description ?? t('meetingManagement.empty.description')}
      />
    )
  }

  const selectedIds = new Set(selection.map((s) => s.id))

  return (
    <div className="mm-cards">
      {rows.map((row) => {
        const selectable = !row.subTask && (row.closable || row.reopenable)
        const checkboxId = `mm-card-select-${row.id}`
        return (
          <article
            key={row.id}
            className={[
              'mm-card',
              row.delayedUpdated ? 'mm-row-overdue' : '',
              row.subTask ? 'mm-row-subtask' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <header className="mm-card-head">
              {/*
                Alt görev satırında kutu HİÇ çizilmez; uygun olmayan ana görevde pasif çizilir.
                Erişilebilir ad satırın ADIYLA kurulur: sabit "Seç" adı, sesli kontrol kullanıcısının
                satırları birbirinden ayırmasını imkânsız kılardı.
              */}
              {selectionEnabled && !row.subTask && (
                <Checkbox
                  inputId={checkboxId}
                  checked={selectedIds.has(row.id)}
                  disabled={!selectable}
                  onChange={(e) => onToggle(row, !!e.checked)}
                  aria-label={t('meetingManagement.select.rowNamed', { name: row.name })}
                  title={
                    selectable ? undefined : t('meetingManagement.select.disabledReason')
                  }
                />
              )}
              {/* Başlık gerçek bir düğmedir → görev detay pop-up'ı (tablodaki satır tıklamasının
                  karşılığı). Overlay-link (`ui-card--linked`) KULLANILMADI: kartta seçim kutusu ve
                  ataç düğmesi de var, kaplama onları örterdi. */}
              <h3 className="mm-card-title mm-cell-name">
                <button
                  type="button"
                  className="act-cell-link"
                  onClick={() => onOpenDetail(row)}
                  aria-label={t('meetingManagement.row.openDetailNamed', { name: row.name })}
                >
                  {row.name}
                </button>
                {/* Görev kaynağı (D/T) — tablodaki Ek kolonundaki işaretin aynısı. Kartta Ek satırı
                    yalnız dosya varken çizildiği için işaret her kartta görünen başlığın sağında. */}
                <TaskOriginMark row={row} />
              </h3>
            </header>

            <dl className="mm-card-fields">
              <dt>{t('meetingManagement.column.description')}</dt>
              <dd className="mm-cell-desc">{row.description ?? '—'}</dd>
              {/*
                SORUMLULAR — tablo ile AYNI bileşen ({@link ResponsiblesLines}): etiket seçimi ve
                biçimlendirme tek yerde durur, iki görünüm ayrışamaz. Kartın boş-değer geleneği
                farklıdır (diğer alanlar gibi tire), bu yüzden `emptyMainText` geçilir.
              */}
              <dt>{t('meetingManagement.column.responsibles')}</dt>
              <dd>
                <ResponsiblesLines row={row} emptyMainText="—" />
              </dd>
              <dt>{t('meetingManagement.column.completionNote')}</dt>
              <dd className="mm-cell-note">{row.completionNote ?? '—'}</dd>
              <dt>{t('meetingManagement.column.taskDuration')}</dt>
              <dd>{taskDurationText(row.taskDurationDays, t) ?? '—'}</dd>
              <dt>{t('meetingManagement.column.lateDays')}</dt>
              <dd>{lateDaysText(row.lateDays, t)}</dd>
              <dt>{t('meetingManagement.column.status')}</dt>
              <dd>{row.statusName ?? '—'}</dd>
              {/*
                EK — tablo dalındaki ataç kolonunun karşılığı, AYNI bileşenle ({@link AttachmentCell}).
                Diğer alanların tersine, dosyası olmayan kartta satır HİÇ çizilmez (tire de konmaz):
                kuralın kendisi "ataç varsa dosya vardır, yoksa işaret de yoktur" — boş bir "Ek: —"
                satırı o kuralı bulanıklaştırır ve kartı da uzatırdı.
              */}
              {row.hasAttachment && (
                <>
                  <dt>{t('meetingManagement.column.attachment')}</dt>
                  <dd>
                    <AttachmentCell row={row} onOpenFiles={onOpenFiles} />
                  </dd>
                </>
              )}
            </dl>
          </article>
        )
      })}

      <Paginator
        first={first}
        rows={rows_}
        totalRecords={totalRecords}
        rowsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(e) => onPage(e.first, e.rows)}
      />
    </div>
  )
}
